import React, { useEffect, useState, useContext, useRef } from 'react';
import { AuthContext } from "../contexts/auth";
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { getReportAttendance, getReportAttendanceOU } from '../services/api_reports';
import { exportAttendanceReports } from '../services/api_extractions';
import ListBoxPageSize from './listbox_pase_size';
import Swal from 'sweetalert2';
import { InformationCircleIcon } from '@heroicons/react/20/solid';
import { estraiMessaggiErrore } from '../services/utils';


const CustomTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  '& .MuiTooltip-tooltip': {
    backgroundColor: '#4F46E5',
    color: '#FFFFFF',
    fontSize: '0.8rem',
  },
});


function Row({ token, row, justificationCodes, startDate, endDate, controller }) {
  const [open, setOpen] = useState(false);
  const [employeeDetails, setEmployeeDetails] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [errorDetails, setErrorDetails] = useState(false);

  const fetchEmployeeDetails = async () => {
    setLoadingDetails(true);
    setErrorDetails(false);
    let ouId = row?.ou?.id;

    if(controller.current) {
      controller.current.abort();
      }
      controller.current = new AbortController();
      const signal = controller.current.signal;

    try {
        const details = await getReportAttendanceOU(
            token,
            { startDate, endDate },
            null,
            1,
            25,
            ouId,
            signal,
            'attendance'
        );
        setEmployeeDetails(details.results);
    } catch (err) {
        if (err?.name != 'CanceledError') {
          console.error('Error fetching employee details:', err);
          setErrorDetails(true);
        }
      } finally {
          setLoadingDetails(false);
      }
  };


  const getJustificationDuration = (code, justification_requests) => {
    const justification = justification_requests.find((request) => request.code === code);
    
    if (!justification) return '';

    if (justification.duration === null) return '';

    const formattedDuration = parseFloat(justification.duration.toFixed(2));

    if (justification.strategy === 'decrease') {
      return `-${formattedDuration}`;
    }

    if (justification.strategy === 'increase') {
      return `+${formattedDuration}`;
    }

    if (justification.strategy === 'null') {
      return <span className='text-gray-400'>"{formattedDuration}"</span>;
    }
  
    return '';
  };

  const getJustificationDescription = (code) => {
  
    const justification = row.justification_requests.find((request) => request.code === code);
    return justification ? justification.description : '';
  };

  return (
    <>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => {
              setOpen(!open);
              if (!open) {
                fetchEmployeeDetails();
              }
            }}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row">
          {row.ou.name}
        </TableCell>
        <TableCell align="right">{row.shift_total}</TableCell>
        <TableCell align="right">{row.shift_net_decrease_justification}</TableCell>
        {justificationCodes?.map((code) => (
          <TableCell key={code} align="right">
            <CustomTooltip title={getJustificationDescription(code)} placement="top">
              <span tabIndex={0}>{getJustificationDuration(code, row.justification_requests)}</span>
            </CustomTooltip>
          </TableCell>
        ))}
        <TableCell align="right">{row.shift_net}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6 + justificationCodes.length} className='bg-indigo-50'>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Typography variant="p" gutterBottom component="div" className='font-semibold text-indigo-800 uppercase'>
                Dettaglio Dipendenti
              </Typography>
              {loadingDetails ? (
                <CircularProgress />
              ) : errorDetails ? (
                <Alert severity="error">Errore nel caricamento dei dettagli</Alert>
              ) : (
                <Table size="small" aria-label="employee details">
                  <TableHead>
                    <TableRow>
                      <TableCell>Dipendente</TableCell>
                      <TableCell align="right">Ore Teoriche</TableCell>
                      <TableCell align="right">Ore Ordinarie</TableCell>
                      {justificationCodes?.map((code) => (
                        <TableCell key={code} align="right">{code}</TableCell>
                      ))}
                      <TableCell align="right">Ore Effettive</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {employeeDetails.length > 0 ? (
                      employeeDetails?.map((detail) => (
                        <TableRow key={detail.employee.id} className={`${detail.is_ou_member ? '' : 'bg-gray-200'}`}>
                          <TableCell>
                            {detail.employee.last_name && detail.employee.first_name 
                              ? `${detail.employee.last_name} ${detail.employee.first_name}` 
                              : detail.employee.cf}
                          </TableCell>
                          <TableCell align="right">{detail.shift_total}</TableCell>
                          <TableCell align="right">{detail.shift_net_decrease_justification}</TableCell>
                          {justificationCodes?.map((code) => {
                            const justification_requests = detail.justification_requests;
                            
                            return (
                            <TableCell key={code} align="right">
                              <CustomTooltip title={getJustificationDescription(code)} placement="top">
                                <span tabIndex={0}>{getJustificationDuration(code, justification_requests)}</span>
                              </CustomTooltip>
                            </TableCell>
                          )})}
                          <TableCell align="right">{detail.shift_net}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6 + justificationCodes.length} align="center">
                          Nessun dettaglio disponibile
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

Row.propTypes = {
  row: PropTypes.shape({
    ou: PropTypes.shape({
      name: PropTypes.string
    }),
    attendance_expected: PropTypes.string,
    attendance_actual: PropTypes.string,
    justification_requests: PropTypes.arrayOf(
      PropTypes.shape({
        code: PropTypes.string,
        name: PropTypes.string,
        duration: PropTypes.number,
        strategy: PropTypes.string
      })
    )
  }),
  justificationCodes: PropTypes.arrayOf(PropTypes.string),
};

export default function CollapsibleTable({ selectedUnitId, startDate, endDate }) {
  const { token } = useContext(AuthContext);
  const [units, setUnits] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [isDownloadError, setIsDownloadError] = useState(false);
  const [justificationCodes, setJustificationCodes] = useState([]);
  const [justificationDescriptions, setJustificationDescriptions] = useState({});
  const [totalResult, setTotalResult] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const controller = useRef(null);
  const [showInfo, setShowInfo] = useState(false);

  useEffect(() => {
    const fetchUnits = async () => {
      setIsLoading(true);
      setError(false);

      try {
        const selectedUnitParam = selectedUnitId !== null ? selectedUnitId : undefined;
        if(controller.current) {
          controller.current.abort();
          }
          controller.current = new AbortController();
          const signal = controller.current.signal;
    
        const response = await getReportAttendance(
          token,
          { startDate, endDate },
          selectedUnitParam,
          [],
          page,
          pageSize,
          signal,
          'attendance'
        );

        const totalItems = response.count || 0;
        setTotalResult(totalItems);
        
        const totalPages = Math.ceil(totalItems / pageSize);
        setNextPage(page < totalPages ? page + 1 : null);
        setPrevPage(page > 1 ? page - 1 : null);

        const fetchedUnits = response.results;
        
        const allJustificationCodes = [
          ...new Set(fetchedUnits?.flatMap((unit) => unit?.justification_requests?.map((request) => request.code)))
        ];

        const descriptions = allJustificationCodes.reduce((acc, code) => {
          const justification = fetchedUnits
            .flatMap((unit) => unit?.justification_requests)
            .find((request) => request.code === code);
          acc[code] = justification ? justification.description : '';
          return acc;
        }, {});
        
        setUnits(fetchedUnits);
        setJustificationCodes(allJustificationCodes);
        setJustificationDescriptions(descriptions);
        setIsLoading(false);
      } catch (err) {
        if (err?.name != 'CanceledError') {
          console.error('Error fetching units:', err);
          setError(true);
          setIsLoading(false); 
        }
        
      }
    };

    fetchUnits();
  }, [startDate, endDate, token, page, pageSize, selectedUnitId]);


  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize); 
    setPage(1); 
  };

  const handleNextPage = () => {
    if (page < Math.ceil(totalResult / pageSize)) {
      setPage((prev) => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (page > 1) {
      setPage((prev) => prev - 1);
    }
  };

  const handleExportReports = async (selectedUnit, startDate, endDate) => {
    setIsLoading(true);
    setIsDownloadError(false);
   
    const unitParam = selectedUnit ?? null;

    Swal.fire({
        title: 'Download in corso...',
        allowOutsideClick: false,
        didOpen: () => {
            Swal.showLoading();
        }
    });
    
    try {
        await exportAttendanceReports(token, startDate, endDate, unitParam);
        Swal.fire({
            icon: 'success',
            title: 'Esportazione completata!',
            showConfirmButton: true,
        });
    } catch (error) {
      setIsDownloadError(true);

      const errorMessage = estraiMessaggiErrore(error);
      Swal.fire({
        icon: 'error',
        title: 'Errore',
        text: errorMessage,
      });
    } finally {
        setIsLoading(false);
    }
  };
  

  if (isLoading) {
    return <CircularProgress />;
  }

  if (error) {
    return <Alert severity="error">Errore nel caricamento della sede</Alert>;
  }

  return (
    <>
      <div className='grid grid-cols-1 md:grid-cols-4 pb-5 gap-y-4 md:gap-y-0 md:gap-x-4 items-center'>
        <div className='md:col-span-4 flex justify-between items-center w-full'>
          <ListBoxPageSize value={pageSize} onChange={handlePageSizeChange} />
          <button 
            className='bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-semibold py-1.5 px-4 rounded'
            onClick={() => handleExportReports(selectedUnitId, startDate, endDate)}
          >
            ESPORTA EXCEL
          </button>
        </div>
      </div>
      
      <div className='mb-3 py-2 px-6 rounded-sm shadow-md border border-gray-100 cursor-pointer' onClick={() => setShowInfo(!showInfo)}>
        <InformationCircleIcon className="inline-block me-3 w-5 h-5 text-indigo-800" />
        <span className='text-sm uppercase font-semibold text-indigo-800'>{showInfo ? 'Chiudi istruzioni' : 'Mostra istruzioni'}</span>
        {showInfo && ( 
          <div className='text-justify text-sm mt-4 pb-2'>
            <p>
              Nelle sezioni della tabella principale è possibile visualizzare il totale delle ore associate a ciascuna unità organizzativa, in base al periodo selezionato.
              <br />Le ore non associate ad alcuna unità sono riportate nella riga "Senza Unità".
              <br />Le "Ore Teoriche" corrispondono al totale delle ore dei turni associate all'unità.
              <br />Le "Ore Ordinarie" rappresentano il totale delle ore teoriche al netto dei giustificativi negativi.
              <br />Le "Ore Effettive" sono calcolate sommando alle ore teoriche i giustificativi di incremento e sottraendo i giustificativi di decremento.
              <br />La somma di ogni giustificativo associato all'Unità viene identificata tramite il proprio codice e visualizzata con i simboli +/- in base all'azione che svolge sulle ore lavorative.
              <br />Selezionando il simbolo "⌄" accanto a ciascuna unità organizzativa, è possibile visualizzare il dettaglio delle ore e verificare come sono distribuite tra i dipendenti. Le ore assegnate a un'unità ma associate a dipendenti che non ne fanno parte saranno evidenziate con uno sfondo grigio. 
            </p>
          </div>
        )}
      </div>

      <TableContainer component={Paper}>
        <Table aria-label="collapsible table">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Unità Operativa</TableCell>
              <TableCell align="right">Ore Teoriche</TableCell>
              <TableCell align="right">Ore Ordinarie</TableCell>
              {justificationCodes?.map((code) => (
                <TableCell key={code} align="right" className='cursor-pointer'>
                  <CustomTooltip title={justificationDescriptions[code]} placement="top">
                    <span>{code}</span>
                  </CustomTooltip>
                </TableCell>
              ))}
              <TableCell align="right">Ore Effettive</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {units?.map((row) => (
              <Row controller={controller} token={token} key={row.ou.id} row={row} justificationCodes={justificationCodes} startDate={startDate} endDate={endDate} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {totalResult > pageSize && (
        <div className="flex justify-between mt-8 px-8">
          <button
            className={`inline-flex items-center rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 ${
              page === 1 ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            onClick={handlePreviousPage}
            disabled={page === 1}
          >
            Precedente
          </button>
          <span className="text-sm font-medium text-gray-900">
            Pagina {page} di {Math.ceil(totalResult / pageSize)}
          </span>
          <button
            className={`inline-flex items-center rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 ${
              page >= Math.ceil(totalResult / pageSize) ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            onClick={handleNextPage}
            disabled={page >= Math.ceil(totalResult / pageSize)}
          >
            Successivo
          </button>
        </div>
      )}
    </>
  );
}

